import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { CHART_STYLES } from '../constants/chartStyles';

interface SegmentPieChartProps {
  value: number;
  totalSegments?: number;
  activeColor?: string;
  secondaryColor?: string;
}

const createSegmentData = (
  value: number,
  totalSegments: number = 40,
  activeColor: string = '#00BB65',
  secondaryColor: string = '#e5e7eb'
) => {
  const segments = [];
  const segmentValue = (value / 100) * totalSegments;

  for (let i = 0; i < totalSegments; i++) {
    const isActive = i < Math.floor(segmentValue);
    const isPartial = i === Math.floor(segmentValue) && segmentValue % 1 > 0;

    segments.push({
      name: `segment-${i}`,
      value: 1,
      fill: isActive ? activeColor : isPartial ? activeColor : secondaryColor,
      opacity: isPartial ? segmentValue % 1 : 1,
    });
  }

  return segments;
};

const createInnerSegmentData = (
  value: number,
  totalSegments: number = 40,
  activeColor: string = '#00BB65'
) => {
  const segmentValue = (value / 100) * totalSegments;

  return [
    {
      name: 'inner-segment',
      value: segmentValue,
      fill: activeColor,
    },
  ];
};

export default function SegmentPieChart({
  value,
  totalSegments = 30,
  activeColor = '#00BB65',
  secondaryColor = '#e5e7eb',
}: SegmentPieChartProps) {
  const data = createSegmentData(
    value,
    totalSegments,
    activeColor,
    secondaryColor
  );

  const innerData = createInnerSegmentData(value, totalSegments, activeColor);

  return (
    <div
      className={`relative flex items-center justify-center w-full h-full min-w-[${CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}px] min-h-[${CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}px] segment-pie-chart`}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}
        minHeight={CHART_STYLES.DIMENSIONS.MIN_CHART_SIZE}
      >
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="70%"
            outerRadius="100%"
            startAngle={180}
            endAngle={0}
            paddingAngle={4}
            dataKey="value"
            stroke="none"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill}
                opacity={entry.opacity}
              />
            ))}
          </Pie>

          <Pie
            data={innerData}
            cx="50%"
            cy="50%"
            innerRadius="52%"
            outerRadius="55%"
            startAngle={180}
            endAngle={0}
            dataKey="value"
            stroke="none"
          >
            <Cell fill={secondaryColor} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center flex items-center">
          <div className="text-3xl font-bold text-gray-800">
            {value.toFixed(1)}
          </div>
          <div className="text-medium font-bold text-mainText">%</div>
        </div>
      </div>
    </div>
  );
}
